import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useAttribution } from "@/hooks/useAttribution";
import { X, Camera, MapPin, Users, Tag, Calendar, Clock, ChevronRight, ChevronLeft, Check, Loader2, Utensils, Heart, Frown, Meh, Plus, Image as ImageIcon, Store, UtensilsCrossed, AlertCircle, Sparkles, Save, Search, Video, Trash2 } from "lucide-react";
import kudozLogoCircular from "@/assets/kudoz-icon.png";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import type { Restaurant, NotificationPreferences } from "@shared/schema";
import { type SharingSettings, type SharingMode } from "./SharingSettingsSelector";
import { useKudozBreakdown } from "@/hooks/useKudozBreakdown";
import { KudozCelebration } from "./KudozCelebration";
import { InlineKudozDelta } from "./InlineKudozDelta";
import { KudozModule } from "./KudozModule";
import { SectionErrorBoundary } from "./AppErrorBoundary";
import { cn } from "@/lib/utils";
import { RestaurantSearchInput, type SelectedRestaurant } from "./RestaurantSearchInput";
import { useTags, type TagCategory } from "@/hooks/useTags";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { resizeForClassification, resizeForDisplay } from "@/lib/imageUtils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import QuickTagSelector from "./QuickTagSelector";
import TagFriendsPopover, { SelectedFriendChips } from "./TagFriendsPopover";

// Step-based flow similar to CameraFirstReview
type ReviewStep = 'setup' | 'media' | 'review';

// AI classification result type
interface ClassificationResult {
  contentType: 'interior' | 'exterior' | 'food-item' | 'menu' | 'general';
  confidence: number;
  suggestedTags: string[];
  detectedItems?: string[];
  cuisineHint?: string;
  available: boolean;
}

interface OrderData {
  id: string;
  restaurantId?: string;  // Local restaurant UUID (if known)
  eatzyClientId?: string; // Eatzy API client ID (used to lookup local restaurant)
  restaurantName: string;
  restaurantAddress?: string;
  cuisineType?: string;
  type: 'takeout' | 'delivery';
  orderDate: string; // ISO date string
  items: {
    lineItemId?: string;
    itemId: string;
    itemName: string;
    quantity: number;
    price: number;
    options?: string[];
  }[];
}

interface MediaUpload {
  id: string;
  type: 'photo' | 'video';
  url: string;
  file: File;
  contentType: 'interior' | 'exterior' | 'menu-item' | null;
  itemName: string;
  itemRating: 'loved' | 'fine' | 'disliked' | null;
  itemNotes: string;
  classification?: ClassificationResult;
  isClassifying?: boolean;
  menuItemId?: string;
}

interface DishRating {
  id: string;
  itemName: string;
  rating: 'loved' | 'fine' | 'disliked' | null;
  notes: string;
  mediaUrl: string | null;
  mediaType: 'photo' | 'video' | null;
  mediaFile: File | null;
  isTheBest: boolean;
}

interface CreateReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReviewSubmitted?: (reviewData: any) => void;
  restaurantName?: string;
  restaurantId?: string;
  detectedLocation?: string;
  sessionId?: string;
  orderId?: string;
  orderData?: OrderData;
}

// Type for rating reference data from API
interface RatingReference {
  reviewId: string;
  restaurant: string;
  restaurantId: string;
  score: number;
  createdAt: string;
}

// Mock menu items for autocomplete
const MOCK_MENU_ITEMS = [
  'Margherita Pizza',
  'Caesar Salad',
  'Chicken Parmesan',
  'Tiramisu',
  'Bruschetta',
  'Spaghetti Carbonara',
  'Caprese Salad',
  'Gelato Trio',
  'Truffle Pasta',
  'Grilled Salmon',
];

// Type for following list users (for Tag Friends feature)
interface FollowingUser {
  id: string;
  displayName: string | null;
  username: string | null;
  profilePhotoUrl: string | null;
}

// Tag categories structure (excluding 'highlights')

export default function CreateReviewModal({ 
  isOpen, 
  onClose, 
  onReviewSubmitted,
  restaurantName = "",
  restaurantId,
  detectedLocation = "",
  sessionId,
  orderId,
  orderData
}: CreateReviewModalProps) {
  // Step-based flow state
  const [step, setStep] = useState<ReviewStep>('setup');
  const [isClassifying, setIsClassifying] = useState(false);
  
  const [restaurant, setRestaurant] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState<SelectedRestaurant | null>(null);
  const [serviceType, setServiceType] = useState<'dine-in' | 'takeout' | 'delivery'>('dine-in');
  const [orderRating, setOrderRating] = useState([5.0]); // 10-point scale
  const [hasRatedVibe, setHasRatedVibe] = useState(false); // Track first deliberate rating
  const [selectedCuisine, setSelectedCuisine] = useState<string>(""); // Explicit cuisine selection (sourced from restaurant category or user pick)
  const [reviewText, setReviewText] = useState("");
  const [mediaUploads, setMediaUploads] = useState<MediaUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [isRestaurantBest, setIsRestaurantBest] = useState(false);
  const [restaurantBestCategory, setRestaurantBestCategory] = useState('');
  const [itemBestSelections, setItemBestSelections] = useState<{[itemName: string]: boolean}>({});
  const [companionNames, setCompanionNames] = useState<string[]>([]); // Store friend IDs instead of names
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]); // Today's date
  const [sharingSettings, setSharingSettings] = useState<SharingSettings>({
    mode: 'public',
    taggedFriends: []
  });
  const [kudozEarned, setKudozEarned] = useState(0);
  const [previousKudoz, setPreviousKudoz] = useState(0);
  const [kudozAnimating, setKudozAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dishRatings, setDishRatings] = useState<DishRating[]>([]);
  
  // Draft mismatch dialog state: set when a saved draft exists for a different restaurant
  const [draftMismatchPending, setDraftMismatchPending] = useState<{
    draftData: any;
    draftRestaurantName: string;
  } | null>(null);

  // UI state
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [activeMediaIndex, setActiveMediaIndex] = useState<number | null>(null);
  const [pendingFocusDishId, setPendingFocusDishId] = useState<string | null>(null); // For iOS Safari focus restoration
  // Per-media menu search state (keyed by media ID)
  const [menuSearchQuery, setMenuSearchQuery] = useState<Record<string, string>>({});
  const [menuSearchOpen, setMenuSearchOpen] = useState<Record<string, boolean>>({});
  
  // Celebration & feedback state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationLevel, setCelebrationLevel] = useState<'basic' | 'medium' | 'high'>('basic');
  const [showMediaDelta, setShowMediaDelta] = useState(false);
  const [mediaDeltaAmount, setMediaDeltaAmount] = useState(0);
  const [showVibeDelta, setShowVibeDelta] = useState(false);
  const [vibeDeltaAmount, setVibeDeltaAmount] = useState(0);
  const [showItemRatingDelta, setShowItemRatingDelta] = useState(false);
  const [itemRatingDeltaAmount, setItemRatingDeltaAmount] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const dishPhotoInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const dishNameInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const hasInitializedRef = useRef(false); // Track if we've already initialized this modal session
  const { toast } = useToast();
  const { sendAttributionEvent } = useAttribution();
  
  // Get user's primary address for location-biased restaurant search
  const userProfile = useUserProfileOptional();
  const userLatitude = userProfile?.primaryAddress?.latitude;
  const userLongitude = userProfile?.primaryAddress?.longitude;

  const isFromOrder = !!orderData;

  // Fetch restaurant data to get cuisine category
  const { data: restaurantData } = useQuery<Restaurant>({
    queryKey: ['/api/restaurants', restaurantId],
    enabled: !!restaurantId,
  });

  // Tier 2 restaurants have a kudozExternalId — they have a full menu in our system
  const isTier2Restaurant = Boolean(
    selectedRestaurant?.kudozExternalId ?? restaurantData?.kudozExternalId
  );

  // Resolved restaurant ID for menu queries (prefer selectedRestaurant, fall back to prop)
  const resolvedRestaurantId = selectedRestaurant?.id ?? restaurantId;

  // Fetch menu for Tier 2 restaurants when at least one food-item photo is uploaded
  const hasFoodItemUploads = mediaUploads.some(m => m.classification?.contentType === 'food-item');
  const aiClassificationUnavailable = mediaUploads.length > 0 && mediaUploads.some(m => m.classification && m.classification.available === false);
  const showFoodItemSection = hasFoodItemUploads || (aiClassificationUnavailable && mediaUploads.length > 0);

  const dominantContentType = useMemo(() => {
    const classified = mediaUploads.filter(m => m.classification?.contentType && m.classification.contentType !== 'general');
    if (classified.length === 0) return 'general' as const;
    const foodCount = classified.filter(m => m.classification?.contentType === 'food-item').length;
    const interiorCount = classified.filter(m => m.classification?.contentType === 'interior').length;
    const exteriorCount = classified.filter(m => m.classification?.contentType === 'exterior').length;
    if (foodCount >= interiorCount && foodCount >= exteriorCount) return 'food-item' as const;
    if (interiorCount >= exteriorCount) return 'interior' as const;
    return 'exterior' as const;
  }, [mediaUploads]);

  const CONTENT_TYPE_INJECTED_TAGS: Record<string, string[]> = useMemo(() => ({
    'food-item': ['Fresh Ingredients', 'Great Portion Size', 'Worth Every Bite', 'Well-Presented', 'Authentic Flavor'],
    'interior': ['Cozy Atmosphere', 'Lively Vibe', 'Instagrammable', 'Clean', 'Spacious'],
    'exterior': ['Easy Parking', 'Great Location', 'Lovely Patio', 'Nice View', 'Good Signage'],
  }), []);

  const classificationSuggestedTags = useMemo(() => {
    const tags = new Set<string>();
    const injected = CONTENT_TYPE_INJECTED_TAGS[dominantContentType];
    if (injected) {
      injected.forEach(t => tags.add(t));
    }
    mediaUploads.forEach(m => {
      if (m.classification?.suggestedTags) {
        m.classification.suggestedTags.forEach(t => tags.add(t));
      }
    });
    return Array.from(tags);
  }, [mediaUploads, dominantContentType, CONTENT_TYPE_INJECTED_TAGS]);

  useEffect(() => {
    if (mediaUploads.length > 0) {
      const summary = mediaUploads.map(m => ({
        id: m.id,
        contentType: m.classification?.contentType ?? 'pending',
        confidence: m.classification?.confidence ?? 0,
        available: m.classification?.available ?? null,
        isClassifying: m.isClassifying ?? false,
      }));
      console.log('[CreateReviewModal] Classification state:', summary);
    }
  }, [mediaUploads]);

  const { data: menuData, isLoading: isLoadingMenu } = useQuery<{
    categories: { name: string; items: { id: string; name: string; description?: string; imageUrl?: string }[] }[]
  }>({
    queryKey: ['/api/restaurants', resolvedRestaurantId, 'menu'],
    enabled: isOpen && isTier2Restaurant && showFoodItemSection && !!resolvedRestaurantId,
  });

  // Flatten all menu categories into a simple list for searching
  const allMenuItems = useMemo(() => {
    if (!menuData?.categories) return [];
    const items: { id: string; name: string; category: string }[] = [];
    menuData.categories.forEach(cat => {
      cat.items.forEach(item => {
        items.push({ id: item.id, name: item.name, category: cat.name });
      });
    });
    return items;
  }, [menuData]);

  // Fetch user's prior review ratings for comparison notches on slider
  const { data: priorReviews = [] } = useQuery<RatingReference[]>({
    queryKey: ['/api/reviews/rating-references'],
    enabled: isOpen,
  });

  // Fetch user's notification preferences for default sharing visibility
  const userId = userProfile?.profile?.userId;
  const { data: notificationPrefs } = useQuery<NotificationPreferences>({
    queryKey: ['/api/user/notification-preferences'],
    enabled: isOpen && !!userId,
  });

  // Fetch user profile stats to detect first-ever review (for Kudoz breakdown)
  const { data: userStats } = useQuery<{ totalReviews: number }>({
    queryKey: ['/api/user/profile'],
    enabled: isOpen && !!userId,
    select: (data: any) => ({ totalReviews: data?.totalReviews ?? 0 }),
  });
  const isFirstReview = (userStats?.totalReviews ?? 0) === 0;

  // Fetch user's following list for Tag Friends feature
  const { data: followingData } = useQuery<{ users: FollowingUser[], total: number, canView: boolean }>({
    queryKey: ['/api/users', userId, 'following'],
    enabled: isOpen && !!userId,
  });

  // Determine if user is an approved influencer (for 'social' sharing option)
  // TODO: Add proper influencer role check when user schema includes this field
  // Sharing mode is set silently from user's profile privacy settings (no UI selector)

  // Fetch restaurant by Eatzy client ID (for orders from order history)
  // This resolves the Eatzy API client ID to our local restaurant UUID
  interface RestaurantByClientIdResponse {
    id: string;
    name: string;
    category: string | null;
    address: string | null;
    city: string;
    state: string | null;
    zipCode: string | null;
    eatzyClientId: string;
    isActive: boolean;
  }
  
  // Determine if orderData.restaurantId is a numeric Eatzy ID vs a local UUID
  // Eatzy IDs are numeric strings like "462076", local UUIDs contain dashes
  const isNumericEatzyId = orderData?.restaurantId && !orderData?.restaurantId.includes('-') && !isNaN(Number(orderData.restaurantId));
  
  // Debug logging for restaurant resolution
  console.log('[CreateReviewModal] orderData:', orderData ? {
    id: orderData.id,
    eatzyClientId: orderData.eatzyClientId,
    restaurantId: orderData.restaurantId,
    restaurantName: orderData.restaurantName,
    type: orderData.type,
    itemsCount: orderData.items?.length || 0,
    items: orderData.items,
    isNumericEatzyId
  } : null);
  console.log('[CreateReviewModal] Query enabled check:', {
    hasEatzyClientId: !!orderData?.eatzyClientId,
    hasNumericEatzyId: isNumericEatzyId,
    queryByClientIdEnabled: !!orderData?.eatzyClientId,
    queryByEatzyIdEnabled: isNumericEatzyId
  });
  
  // Query by Eatzy client ID (e.g., "nkrof3dbiq7z6xvf") - used when orderData has eatzyClientId
  const { data: restaurantByClientId, isLoading: isLoadingRestaurantByClientId, error: restaurantByClientIdError } = useQuery<RestaurantByClientIdResponse>({
    queryKey: ['/api/restaurants/by-client-id', orderData?.eatzyClientId],
    enabled: !!orderData?.eatzyClientId,
  });
  
  // Query by Eatzy numeric ID (e.g., "462076") - used when orderData comes from order history
  const { data: restaurantByEatzyId, isLoading: isLoadingRestaurantByEatzyId, error: restaurantByEatzyIdError } = useQuery<RestaurantByClientIdResponse>({
    queryKey: ['/api/restaurants/by-eatzy-id', orderData?.restaurantId],
    enabled: !!isNumericEatzyId && !orderData?.eatzyClientId,
  });
  
  // Combined loading state for restaurant resolution
  const isResolvingRestaurant = isLoadingRestaurantByClientId || isLoadingRestaurantByEatzyId;
  const resolvedRestaurant = restaurantByClientId || restaurantByEatzyId;
  
  // Log the query results
  console.log('[CreateReviewModal] Restaurant lookup query results:', {
    byClientId: restaurantByClientId,
    byEatzyId: restaurantByEatzyId,
    resolvedRestaurant,
    isLoading: isResolvingRestaurant,
    error: restaurantByClientIdError || restaurantByEatzyIdError
  });

  // Fetch tags from database
  const { tagCategories, getTagsByType, isLoading: isLoadingTags } = useTags();
  
  // Get cuisine tags from database
  const cuisineTags = getTagsByType('cuisine');

  // Helper function to initialize selectedRestaurant from props
  // Returns { name: string, hasPropsRestaurant: boolean, category: string | null }
  const initializeSelectedRestaurant = (): { name: string; hasPropsRestaurant: boolean; category: string | null } => {
    // Prioritize loaded restaurantData if available (direct restaurant ID provided)
    if (restaurantData && restaurantId) {
      setSelectedRestaurant({
        id: restaurantData.id,
        name: restaurantData.name,
        address: restaurantData.address || '',
        city: restaurantData.city,
        state: restaurantData.state || '',
        googlePlaceId: restaurantData.googlePlaceId || null,
        isNew: false,
        category: restaurantData.category || null,
      });
      return { name: restaurantData.name, hasPropsRestaurant: true, category: restaurantData.category || null };
    }
    // Use restaurant resolved from Eatzy (either by client ID or numeric Eatzy ID from order history)
    else if (resolvedRestaurant) {
      setSelectedRestaurant({
        id: resolvedRestaurant.id,
        name: resolvedRestaurant.name,
        address: resolvedRestaurant.address || '',
        city: resolvedRestaurant.city,
        state: resolvedRestaurant.state || '',
        googlePlaceId: null,
        isNew: false,
        category: resolvedRestaurant.category,
      });
      return { name: resolvedRestaurant.name, hasPropsRestaurant: true, category: resolvedRestaurant.category };
    }
    // Use orderData's restaurantId if it's a valid local UUID (not a numeric Eatzy ID)
    else if (orderData?.restaurantId && orderData?.restaurantName && !isNumericEatzyId) {
      setSelectedRestaurant({
        id: orderData.restaurantId,
        name: orderData.restaurantName,
        address: '',
        city: '',
        state: '',
        googlePlaceId: null,
        isNew: false,
        category: null,
      });
      return { name: orderData.restaurantName, hasPropsRestaurant: true, category: null };
    }
    // No props - don't clear selection yet (draft might have one)
    return { name: orderData?.restaurantName || restaurantName || '', hasPropsRestaurant: false, category: null };
  };

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen]);

  // Initialize step based on whether this is an order-based review
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      // For order-based reviews, skip to review step (restaurant already known)
      // For new reviews, start at setup step
      if (orderData) {
        setStep('review');
      } else {
        setStep('setup');
      }
    }
  }, [isOpen, orderData]);

  // Reset and pre-populate when modal opens
  useEffect(() => {
    if (isOpen) {
      // First, always initialize selectedRestaurant and get the restaurant name from props
      const { name: restaurantNameFromProps, hasPropsRestaurant } = initializeSelectedRestaurant();
      
      // Only do full form reset on initial open, not on subsequent data updates
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        
        // Check for saved draft
        const savedDraft = localStorage.getItem('eatzy-review-draft');
        let shouldRestoreDraft = false;
        let draftData: any = null;
        
        if (savedDraft) {
          try {
            draftData = JSON.parse(savedDraft);
            const currentOrderId = orderData?.id || null;
            const draftOrderId = draftData.orderId || null;

            // Determine the current restaurant's identifier using UUID-safe sources only.
            // Priority: prop restaurantId (always UUID) → resolvedRestaurant?.id (async UUID) →
            // orderData.restaurantId ONLY when it is not a numeric Eatzy ID (isNumericEatzyId
            // is already computed above; a non-numeric orderData.restaurantId is a local UUID).
            // Numeric Eatzy IDs are excluded — they are incompatible with the UUID values stored
            // in drafts. If all sources are unavailable (async still loading, numeric-only ID),
            // currentRestaurantId is null and the mismatch guard won't fire, preserving the
            // safe orderId-only check (same as pre-fix behavior).
            const currentRestaurantId =
              restaurantId ||
              resolvedRestaurant?.id ||
              (!isNumericEatzyId && orderData?.restaurantId ? orderData.restaurantId : null) ||
              null;
            // Draft's restaurant ID — present in drafts saved after this fix;
            // absent in legacy drafts (treated as unknown = mismatch if we have a current ID to compare).
            const draftRestaurantId: string | null = draftData.restaurantId || null;

            // Step 1: orderId compatibility check
            let orderIdOk: boolean;
            if (currentOrderId && draftOrderId) {
              orderIdOk = currentOrderId === draftOrderId;
            } else if (!currentOrderId && !draftOrderId) {
              orderIdOk = true; // Both are Dine Out / manual reviews
            } else {
              orderIdOk = false; // One has orderId, other doesn't
            }

            if (!orderIdOk) {
              // Different order context — never restore
              shouldRestoreDraft = false;
            } else if (currentRestaurantId && draftRestaurantId !== currentRestaurantId) {
              // We know which restaurant is being opened and it doesn't match the draft
              // (includes legacy drafts with no restaurantId — safer to confirm than silently mix).
              const draftRestaurantName =
                draftData.restaurant ||
                draftData.selectedRestaurant?.name ||
                'another restaurant';
              setDraftMismatchPending({ draftData, draftRestaurantName });
              // Leave form uninitialized — the dialog will trigger initialization after user decides.
              // hasInitializedRef stays true so this effect doesn't fire again.
              shouldRestoreDraft = false;
              // Signal that we handled initialization via dialog — skip the else branch below.
              draftData = null;
            } else {
              // Same restaurant (or no pre-selected restaurant to compare against) — safe to restore
              shouldRestoreDraft = true;
            }

            console.log('[CreateReviewModal] Draft restore check:', {
              currentOrderId,
              draftOrderId,
              currentRestaurantId,
              draftRestaurantId,
              shouldRestoreDraft,
            });
          } catch (error) {
            console.error('Failed to parse draft:', error);
            shouldRestoreDraft = false;
          }
        }
        
        if (shouldRestoreDraft && draftData) {
          try {
            // For restaurant: use props first (they're authoritative), then draft, then fallback
            const finalRestaurantName = restaurantNameFromProps || draftData.restaurant || '';
            setRestaurant(finalRestaurantName);
            
            // If no props initialized selectedRestaurant but draft has one, restore it
            if (!hasPropsRestaurant && draftData.selectedRestaurant?.id) {
              // Ensure backward compatibility with legacy drafts missing 'category' field
              setSelectedRestaurant({
                ...draftData.selectedRestaurant,
                category: draftData.selectedRestaurant.category ?? null,
              });
            } else if (!hasPropsRestaurant) {
              // No props and no draft restaurant - clear selection
              setSelectedRestaurant(null);
            }
            
            setServiceType(draftData.serviceType || orderData?.type || 'dine-in');
            const restoredRating = draftData.orderRating || [5.0];
            setOrderRating(restoredRating);
            setHasRatedVibe(restoredRating[0] !== 5.0); // Mark as rated if not default
            setSelectedCuisine(draftData.selectedCuisine || "");
            setReviewText(draftData.reviewText || "");
            setTags(draftData.tags || []);
            setCompanionNames(draftData.companionNames || []);
            setVisitDate(draftData.visitDate || new Date().toISOString().split('T')[0]);
            setSharingSettings(draftData.sharingSettings || { mode: 'public', taggedFriends: [] });
            setIsRestaurantBest(draftData.isRestaurantBest || false);
            setRestaurantBestCategory(draftData.restaurantBestCategory || '');
            setDishRatings(draftData.dishRatings?.map((dish: any) => ({
              ...dish,
              mediaUrl: null, // Media URLs can't be restored from draft
              mediaType: null,
              mediaFile: null
            })) || []);
            
            toast({
              title: "Draft Restored",
              description: "Your saved review draft has been loaded.",
            });
          } catch (error) {
            console.error('Failed to load draft:', error);
            // Fall back to defaults
            setRestaurant(restaurantNameFromProps);
            if (!hasPropsRestaurant) setSelectedRestaurant(null);
            setServiceType(orderData?.type || 'dine-in');
            setOrderRating([5.0]);
            setHasRatedVibe(false);
            setSelectedCuisine("");
            setVisitDate(new Date().toISOString().split('T')[0]);
          }
        } else {
          // No draft - use defaults and initialize from props
          setRestaurant(restaurantNameFromProps);
          if (!hasPropsRestaurant) setSelectedRestaurant(null);
          setServiceType(orderData?.type || 'dine-in');
          setVisitDate(new Date().toISOString().split('T')[0]);
          setDishRatings([]); // Clear dish ratings when opening fresh
          setOrderRating([5.0]);
          setHasRatedVibe(false);
          setSelectedCuisine("");
          setReviewText("");
          setTags([]);
          setCompanionNames([]);
          setIsRestaurantBest(false);
        }
      }
    }
  }, [isOpen, orderData, restaurantName, restaurantData, restaurantId, resolvedRestaurant, isNumericEatzyId, toast]);

  // Auto-strip invalid tags when serviceType changes.
  // Filters at both category level (experienceTypes) AND individual tag level (orderTypes)
  // so that tags from mixed-applicability categories are also stripped correctly.
  useEffect(() => {
    const validTags = tagCategories
      .filter(category => category.experienceTypes.includes(serviceType))
      .flatMap(category =>
        category.tags
          .filter(tag => tag.orderTypes.includes(serviceType))
          .map(tag => tag.label)
      );
    setTags(prev => prev.filter(tag => validTags.includes(tag)));
  }, [serviceType, tagCategories]);

  // Helper to map category names to cuisine tags
  const mapCategoryToCuisineTag = (category: string | null): string | null => {
    if (!category) return null;
    
    const categoryLower = category.toLowerCase().trim();
    
    // Common mappings from Google Places/Eatzy categories to our cuisine tags
    const categoryMappings: Record<string, string> = {
      // Google Places types
      'chinese restaurant': 'Chinese',
      'japanese restaurant': 'Japanese',
      'thai restaurant': 'Thai',
      'indian restaurant': 'Indian',
      'mexican restaurant': 'Mexican',
      'italian restaurant': 'Italian',
      'american restaurant': 'American',
      'seafood restaurant': 'Seafood',
      'mediterranean restaurant': 'Mediterranean',
      'barbecue restaurant': 'BBQ',
      'vegetarian restaurant': 'Vegetarian',
      'vegan restaurant': 'Vegan',
      // Simple mappings
      'chinese': 'Chinese',
      'japanese': 'Japanese',
      'thai': 'Thai',
      'indian': 'Indian',
      'mexican': 'Mexican',
      'italian': 'Italian',
      'american': 'American',
      'seafood': 'Seafood',
      'mediterranean': 'Mediterranean',
      'bbq': 'BBQ',
      'barbecue': 'BBQ',
      'vegetarian': 'Vegetarian',
      'vegan': 'Vegan',
      'asian': 'Asian',
      'southern': 'Southern',
      'fusion': 'Fusion',
      // Eatzy-style categories
      'asian cuisine': 'Asian',
      'chinese cuisine': 'Chinese',
      'japanese cuisine': 'Japanese',
      'thai cuisine': 'Thai',
      'indian cuisine': 'Indian',
      'sushi': 'Japanese',
      'pizza': 'Italian',
      'tacos': 'Mexican',
      'burgers': 'American',
    };
    
    // Check for exact match first
    if (categoryMappings[categoryLower]) {
      return categoryMappings[categoryLower];
    }
    
    // Check if category contains any of our cuisine keywords
    for (const cuisineTag of cuisineTags) {
      if (categoryLower.includes(cuisineTag.label.toLowerCase())) {
        return cuisineTag.label;
      }
    }
    
    return null;
  };

  // Auto-select cuisine tags from restaurant category for all service types
  // Also sets selectedCuisine so the requirement gate stays consistent with tags[]
  useEffect(() => {
    // Use selectedRestaurant.category if available, otherwise fall back to restaurantData, then orderData.cuisineType
    const category = selectedRestaurant?.category || restaurantData?.category || orderData?.cuisineType;
    
    if (isOpen && category) {
      const savedDraft = localStorage.getItem('eatzy-review-draft');
      // Only pre-select if not restoring from draft
      if (!savedDraft) {
        const matchingTag = mapCategoryToCuisineTag(category);
        
        if (matchingTag) {
          setSelectedCuisine(matchingTag);
          if (!tags.includes(matchingTag)) {
            setTags(prev => [...prev, matchingTag]);
          }
        }
      }
    }
  }, [isOpen, restaurantData, selectedRestaurant, orderData, cuisineTags]);

  // Sync selectedCuisine from restaurant category sources (controlled state)
  // Clears when restaurant changes to one with no detectable category so picker re-appears
  useEffect(() => {
    if (!isOpen) return;
    const category = selectedRestaurant?.category || restaurantData?.category || orderData?.cuisineType;
    const matched = category ? mapCategoryToCuisineTag(category) : null;
    setSelectedCuisine(matched || "");
    if (!matched) {
      // Also strip any stale cuisine tags so QuickTagSelector stays consistent
      setTags(prev => prev.filter(t => !cuisineTags.some(ct => ct.label === t)));
    }
  }, [isOpen, selectedRestaurant, restaurantData, orderData, cuisineTags]);

  // When restaurant is resolved from Eatzy client ID, update selectedRestaurant and auto-select cuisine tag
  useEffect(() => {
    if (isOpen && restaurantByClientId && orderData?.eatzyClientId && cuisineTags.length > 0) {
      console.log('[CreateReviewModal] Client ID resolution effect running:', {
        restaurantId: restaurantByClientId.id,
        restaurantName: restaurantByClientId.name,
        category: restaurantByClientId.category,
        cuisineTagsCount: cuisineTags.length
      });
      
      // Update selectedRestaurant with resolved data
      setSelectedRestaurant({
        id: restaurantByClientId.id,
        name: restaurantByClientId.name,
        address: restaurantByClientId.address || '',
        city: restaurantByClientId.city,
        state: restaurantByClientId.state || '',
        googlePlaceId: null,
        isNew: false,
        category: restaurantByClientId.category,
      });
      setRestaurant(restaurantByClientId.name);

      // Auto-select cuisine tag from resolved restaurant category
      if (restaurantByClientId.category) {
        const matchingTag = mapCategoryToCuisineTag(restaurantByClientId.category);
        console.log('[CreateReviewModal] Cuisine tag mapping:', {
          category: restaurantByClientId.category,
          matchingTag
        });
        if (matchingTag) {
          setTags(prev => {
            const newTags = prev.includes(matchingTag) ? prev : [...prev, matchingTag];
            console.log('[CreateReviewModal] Setting tags:', { prev, newTags });
            return newTags;
          });
        }
      }
    }
  }, [isOpen, restaurantByClientId, orderData?.eatzyClientId, cuisineTags]);

  // Auto-populate dish ratings from order items for Takeout/Delivery
  useEffect(() => {
    console.log('[CreateReviewModal] Auto-populate dishes effect:', {
      isOpen,
      serviceType,
      hasOrderItems: !!orderData?.items,
      orderItemsCount: orderData?.items?.length || 0,
      dishRatingsLength: dishRatings.length
    });
    
    // For order-based reviews, always populate dishes from order items
    // This takes priority over any saved draft since the order items are the source of truth
    if (isOpen && (serviceType === 'takeout' || serviceType === 'delivery') && orderData?.items && orderData.items.length > 0) {
      // Only auto-populate if dishes haven't been set yet
      if (dishRatings.length === 0) {
        console.log('[CreateReviewModal] Populating dish ratings from order items:', orderData.items);
        const orderItems = orderData.items as Array<{lineItemId?: string; itemId: string; itemName: string; quantity: number; price: number; options?: string[]; specialInstructions?: string}>;
        const initialDishes: DishRating[] = orderItems.map(item => ({
          id: item.lineItemId || item.itemId,
          itemName: item.itemName,
          rating: null,
          notes: '',
          mediaUrl: null,
          mediaType: null,
          mediaFile: null,
          isTheBest: false
        }));
        console.log('[CreateReviewModal] Setting dishRatings to:', initialDishes);
        setDishRatings(initialDishes);
      }
    }
  }, [isOpen, serviceType, orderData, dishRatings.length]);

  // Get Kudoz breakdown using hook
  const kudozBreakdown = useKudozBreakdown({
    hasVibeRating: hasRatedVibe,
    mediaUploads,
    dishRatings,
    sharingSettings,
    isFirstReview,
  });

  // Requirements gate booleans (all must pass to enable Post)
  const hasRestaurant = !!selectedRestaurant?.id;
  const hasCuisine = selectedCuisine.length > 0;
  const hasTag = tags.length >= 1;
  const hasText = reviewText.trim().length >= 10;
  const hasSlider = true; // Slider is always present; satisfies requirement automatically
  const canPost = hasRestaurant && hasCuisine && hasTag && hasText && hasSlider;

  // Update kudozEarned when breakdown changes
  useEffect(() => {
    // Trigger animation if Kudoz increased
    if (kudozBreakdown.earned > previousKudoz) {
      setKudozAnimating(true);
      setTimeout(() => setKudozAnimating(false), 600);
    }
    
    setPreviousKudoz(kudozBreakdown.earned);
    setKudozEarned(kudozBreakdown.earned);
  }, [kudozBreakdown.earned, previousKudoz]);
  
  // iOS Safari: Focus dish name input after photo upload
  // Using useEffect ensures the ref exists after re-render
  useEffect(() => {
    if (pendingFocusDishId && serviceType === 'dine-in') {
      const inputRef = dishNameInputRefs.current[pendingFocusDishId];
      if (inputRef) {
        // Small delay to ensure iOS Safari has fully returned from file picker
        const timeoutId = setTimeout(() => {
          inputRef.focus();
          inputRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
        setPendingFocusDishId(null);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [pendingFocusDishId, serviceType]);

  // Track if sharing settings have been initialized from preferences
  const [sharingInitialized, setSharingInitialized] = useState(false);

  // Initialize sharing settings based on user's privacy preferences
  useEffect(() => {
    if (isOpen && notificationPrefs && !sharingInitialized) {
      // Map user's profileVisibility setting to sharing mode
      // 'public' -> 'public', 'friends' -> 'friends', 'private' -> 'private'
      // Default to 'public' if not set
      const defaultMode: SharingMode = (notificationPrefs.profileVisibility as SharingMode) || 'public';
      setSharingSettings(prev => ({
        ...prev,
        mode: defaultMode
      }));
      setSharingInitialized(true);
    }
  }, [isOpen, notificationPrefs, sharingInitialized]);

  // Reset sharing initialized flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSharingInitialized(false);
    }
  }, [isOpen]);

  // AI classification function (reused from CameraFirstReview)
  const classifyMediaItem = useCallback(async (file: File, type: 'photo' | 'video', url: string): Promise<ClassificationResult> => {
    const defaultResult: ClassificationResult = {
      contentType: 'general',
      confidence: 0,
      suggestedTags: [],
      available: false,
    };
    
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      if (type === 'video') {
        const video = document.createElement('video');
        video.src = url;
        video.currentTime = 1;
        
        await new Promise((resolve) => {
          video.onloadeddata = resolve;
        });
        
        const canvas = document.createElement('canvas');
        const maxDim = 512;
        const scale = Math.min(maxDim / video.videoWidth, maxDim / video.videoHeight);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const frameBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        
        const response = await fetch('/api/media/classify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ imageBase64: frameBase64, type: 'video' }),
        });
        
        if (response.ok) {
          return await response.json() as ClassificationResult;
        }
        return defaultResult;
      } else {
        const { base64 } = await resizeForClassification(file);
        
        const response = await fetch('/api/media/classify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ imageBase64: base64, type: 'image' }),
        });
        
        if (response.ok) {
          return await response.json() as ClassificationResult;
        }
        return defaultResult;
      }
    } catch (error) {
      console.error('[CreateReviewModal] Classification error:', error);
      return defaultResult;
    }
  }, []);

  const resetForm = () => {
    mediaUploads.forEach(media => URL.revokeObjectURL(media.url));
    dishRatings.forEach(dish => {
      if (dish.mediaUrl) URL.revokeObjectURL(dish.mediaUrl);
    });
    // Reset step-based flow
    setStep('setup');
    setIsClassifying(false);
    setServiceType('dine-in');
    setOrderRating([5.0]);
    setHasRatedVibe(false);
    setSelectedCuisine("");
    setReviewText("");
    setMediaUploads([]);
    setTags([]);
    setIsRestaurantBest(false);
    setSelectedRestaurant(null);
    setRestaurantBestCategory('');
    setItemBestSelections({});
    setCompanionNames([]);
    setVisitDate(new Date().toISOString().split('T')[0]);
    setSharingSettings({ mode: 'public', taggedFriends: [] });
    setKudozEarned(0);
    setPreviousKudoz(0);
    setDishRatings([]);
    setActiveMediaIndex(null);
    // Clear saved draft since review is being submitted
    localStorage.removeItem('eatzy-review-draft');
    window.dispatchEvent(new Event('draftChanged'));
  };

  const handleSaveDraft = () => {
    const draftData = {
      orderId: orderData?.id || null,
      // restaurantId is the stable identifier used to detect cross-restaurant draft mismatches.
      // UUID-safe priority chain: prop restaurantId → selectedRestaurant.id (local UUID from DB) →
      // orderData.restaurantId when it is not a numeric Eatzy ID (isNumericEatzyId guards this).
      // Numeric Eatzy IDs are excluded — they would corrupt UUID comparisons on restore.
      restaurantId:
        restaurantId ||
        selectedRestaurant?.id ||
        (!isNumericEatzyId && orderData?.restaurantId ? orderData.restaurantId : null) ||
        null,
      restaurant,
      selectedRestaurant: selectedRestaurant ? {
        id: selectedRestaurant.id,
        name: selectedRestaurant.name,
        address: selectedRestaurant.address,
        city: selectedRestaurant.city,
        state: selectedRestaurant.state,
        googlePlaceId: selectedRestaurant.googlePlaceId,
        isNew: false, // Always mark as not new when restoring
        category: selectedRestaurant.category,
      } : null,
      serviceType,
      orderRating,
      selectedCuisine,
      reviewText,
      tags,
      companionNames,
      visitDate,
      sharingSettings,
      isRestaurantBest,
      restaurantBestCategory,
      dishRatings: dishRatings.map(dish => ({
        id: dish.id,
        itemName: dish.itemName,
        rating: dish.rating,
        notes: dish.notes,
        isTheBest: dish.isTheBest
        // Note: mediaUrl/File not saved in draft as they're temporary object URLs
      })),
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('eatzy-review-draft', JSON.stringify(draftData));
    window.dispatchEvent(new Event('draftChanged'));
    
    toast({
      title: "Draft Saved",
      description: "Your review has been saved. You can complete it later.",
    });
    
    onClose();
  };

  const handleSubmit = async () => {
    if (!canPost) return;

    setIsSubmitting(true);
    
    // Capture Kudoz earned value before setTimeout to avoid stale closure
    const earnedKudoz = kudozBreakdown.earned;

    try {
      // Step 1: Upload all media files first to get permanent URLs
      const filesToUpload: Array<{ file: File; blobUrl: string; mediaUploadId?: string; dishIndex?: number }> = [];
      
      // Collect vibe/ambiance media files with their blob URLs
      for (const media of mediaUploads) {
        if (media.file) {
          filesToUpload.push({ file: media.file, blobUrl: media.url, mediaUploadId: media.id });
        }
      }
      
      // Collect dish rating media files
      for (let i = 0; i < dishRatings.length; i++) {
        const dish = dishRatings[i];
        if (dish.mediaFile && dish.mediaUrl) {
          filesToUpload.push({ file: dish.mediaFile, blobUrl: dish.mediaUrl, dishIndex: i });
        }
      }
      
      // Upload files one at a time to the CDN endpoint - simpler and more reliable
      const uploadedUrlMap = new Map<string, string>(); // Maps blob URL to permanent CDN URL

      if (filesToUpload.length > 0) {
        const token = localStorage.getItem('auth_token');

        for (const item of filesToUpload) {
          const isVideo = item.file.type.startsWith('video/');
          let fileToSend = item.file;

          if (!isVideo) {
            try {
              const resized = await resizeForDisplay(item.file, 1200);
              const baseName = item.file.name.replace(/\.[^.]+$/, '');
              fileToSend = new File([resized.blob], `${baseName}.jpg`, { type: 'image/jpeg' });
            } catch (resizeErr) {
              console.warn('[Upload] Image resize failed, uploading original:', resizeErr);
            }
          }

          const formData = new FormData();
          formData.append('file', fileToSend);

          console.log(`[Upload] Uploading ${fileToSend.name} (${fileToSend.size}B) to CDN`);
          const eatzyToken = localStorage.getItem('eatzyUserToken') ?? '';
          const uploadResponse = await fetch('/api/media/upload-cdn', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
              ...(eatzyToken ? { 'x-eatzy-token': eatzyToken } : {}),
            },
            body: formData,
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to upload ${fileToSend.name}`);
          }

          const uploadResult = await uploadResponse.json();
          console.log(`[Upload] CDN result for ${fileToSend.name}:`, uploadResult);
          uploadedUrlMap.set(item.blobUrl, uploadResult.url);
        }
      }
      
      // Step 2: Build review data with permanent URLs
      // Safety guard - canPost should have already caught this, but guard against race conditions
      if (!selectedRestaurant?.id) {
        setIsSubmitting(false);
        return;
      }
      
      const finalRestaurantId = selectedRestaurant.id;
      const finalRestaurantName = selectedRestaurant.name;
      
      const reviewData = {
        restaurantId: finalRestaurantId,
        restaurantName: finalRestaurantName,
        rating: orderRating[0],
        text: reviewText || "",
        tags: tags.length > 0 ? tags : [],
        media: [
          ...mediaUploads.map(m => ({
            type: m.type === 'video' ? 'video' : 'photo',
            url: uploadedUrlMap.get(m.url) || m.url, // Use permanent URL if available
            contentType: m.contentType,
            itemName: m.itemName || null,
            itemRating: m.itemRating || null,
            itemNotes: m.itemNotes || null
          })),
          ...dishRatings.filter(d => d.mediaUrl).map(d => ({
            type: d.mediaType === 'video' ? 'video' : 'photo',
            url: uploadedUrlMap.get(d.mediaUrl!) || d.mediaUrl, // Use permanent URL if available
            contentType: 'menu-item',
            itemName: d.itemName,
            itemRating: d.rating,
            itemNotes: d.notes
          }))
        ],
        companions: companionNames.length > 0 ? companionNames : null,
        visitDate,
        sharingLevel: sharingSettings.mode,
        kudozEarned: earnedKudoz, // Use captured value to ensure 0 for private reviews
        sessionId: sessionId || null,
        orderId: orderId || orderData?.id || null,
        theBest: isRestaurantBest ? { category: restaurantData?.category || 'restaurant' } : null,
        dishRatings: dishRatings.length > 0 ? dishRatings.map(dish => ({
          itemId: dish.id, // Menu item ID (e.g., "I463973") for linking to menu items
          itemName: dish.itemName,
          rating: dish.rating,
          notes: dish.notes,
          mediaUrl: uploadedUrlMap.get(dish.mediaUrl!) || dish.mediaUrl,
          mediaType: dish.mediaType === 'photo' ? 'image' : dish.mediaType,
          isTheBest: dish.isTheBest
        })) : null
      };
      
      // Step 3: Submit review to API
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(reviewData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit review');
      }
      
      const result = await response.json();
      console.log('Review created successfully:', result);
      
      sendAttributionEvent('review_written', 'review', result.id ?? result.review?.id ?? '');
      
      setIsSubmitting(false);
      
      // Private reviews (0 Kudoz) get subtle confirmation instead of celebration
      if (earnedKudoz === 0) {
        toast({
          title: "Review posted privately",
          description: "0 Kudoz earned",
        });
        
        setTimeout(() => {
          onReviewSubmitted?.(result);
          resetForm();
          onClose();
        }, 300);
      } else {
        // Effort-weighted celebration for shared reviews
        // Use the server-reported amount (source of truth) if available, fall back to hook estimate
        const actualKudoz = result.kudozEarned ?? earnedKudoz;
        // Update the displayed Kudoz badge to match the real server-awarded amount
        if (result.kudozEarned != null) {
          setKudozEarned(result.kudozEarned);
        }
        const completionLevel = actualKudoz >= 40 ? 'high' : actualKudoz >= 25 ? 'medium' : 'basic';
        setCelebrationLevel(completionLevel);
        setShowCelebration(true);
        
        // Auto-dismiss celebration after appropriate duration
        const celebrationDuration = completionLevel === 'high' ? 2500 : completionLevel === 'medium' ? 2000 : 1500;
        setTimeout(() => {
          setShowCelebration(false);
          setTimeout(() => {
            onReviewSubmitted?.(result);
            resetForm();
            onClose();
          }, 300);
        }, celebrationDuration);
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      setIsSubmitting(false);
      toast({
        title: "Failed to submit review",
        description: error.message || "Please try again later.",
        variant: "destructive"
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setIsClassifying(true);
    
    try {
      for (const file of Array.from(files)) {
        if (file.size > 50 * 1024 * 1024) {
          toast({
            title: "File Too Large",
            description: "Please select files smaller than 50MB.",
            variant: "destructive"
          });
          continue;
        }

        const type = file.type.startsWith('video/') ? 'video' : 'photo';
        const url = URL.createObjectURL(file);
        const mediaId = `media-${Date.now()}-${Math.random()}`;
        
        // Add media with isClassifying flag
        const newMedia: MediaUpload = {
          id: mediaId,
          type,
          url,
          file,
          contentType: null,
          itemName: '',
          itemRating: null,
          itemNotes: '',
          isClassifying: true
        };
        
        setMediaUploads(prev => [...prev, newMedia]);
        
        // Run AI classification
        const classification = await classifyMediaItem(file, type, url);
        console.log('[CreateReviewModal] Classification result:', { mediaId, contentType: classification.contentType, confidence: classification.confidence, available: classification.available, detectedItems: classification.detectedItems, suggestedTags: classification.suggestedTags });
        
        // Map classification contentType to MediaUpload contentType
        let mappedContentType: 'interior' | 'exterior' | 'menu-item' | null = null;
        if (classification.contentType === 'food-item') {
          mappedContentType = 'menu-item';
        } else if (classification.contentType === 'interior' || classification.contentType === 'exterior') {
          mappedContentType = classification.contentType;
        }
        
        // Update media with classification result
        setMediaUploads(prev => prev.map(m => 
          m.id === mediaId 
            ? { 
                ...m, 
                classification,
                contentType: mappedContentType,
                itemName: classification.detectedItems?.[0] || '',
                isClassifying: false
              } 
            : m
        ));
      }
      
      // Trigger inline delta feedback
      const kudozFromFiles = Array.from(files).reduce((total, file) => {
        return total + (file.type.startsWith('video/') ? 10 : 5);
      }, 0);
      
      setMediaDeltaAmount(kudozFromFiles);
      setShowMediaDelta(true);
      setTimeout(() => setShowMediaDelta(false), 2200);
      
      // Move to review step after adding media
      if (step === 'media') {
        setStep('review');
      }
      
    } catch (error) {
      toast({
        title: "Upload Error",
        description: "Failed to upload media. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setIsClassifying(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeMedia = (id: string) => {
    const media = mediaUploads.find(m => m.id === id);
    if (media) {
      URL.revokeObjectURL(media.url);
    }
    setMediaUploads(prev => prev.filter(m => m.id !== id));
  };

  const updateMediaContent = (id: string, updates: Partial<MediaUpload>) => {
    setMediaUploads(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  // Use order items if available, otherwise fall back to mock menu items
  const availableMenuItems = orderData?.items.length 
    ? orderData.items.map(item => item.itemName)
    : MOCK_MENU_ITEMS;

  const filteredMenuItems = itemSearchTerm
    ? availableMenuItems.filter(item =>
        item.toLowerCase().includes(itemSearchTerm.toLowerCase())
      )
    : availableMenuItems;

  // Common tags for quick selection
  const commonTags = [
    'date-night', 'family-friendly', 'quick-service', 'authentic', 
    'healthy', 'comfort-food', 'vegetarian', 'spicy', 'worth-it', 'hidden-gem'
  ];

  // Current restaurant display name (for the mismatch dialog's "new restaurant" label)
  const currentRestaurantDisplayName =
    restaurantName || orderData?.restaurantName || 'this restaurant';

  return (
    <>
    {/* Draft mismatch dialog — shown when a saved draft is for a different restaurant */}
    <AlertDialog open={draftMismatchPending !== null}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unfinished review saved</AlertDialogTitle>
          <AlertDialogDescription>
            You have a saved draft for{' '}
            <strong>{draftMismatchPending?.draftRestaurantName}</strong>. Starting a
            new review for <strong>{currentRestaurantDisplayName}</strong> will
            discard that draft. You can also cancel and go back to finish your
            previous review instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setDraftMismatchPending(null);
              onClose();
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              localStorage.removeItem('eatzy-review-draft');
              window.dispatchEvent(new Event('draftChanged'));
              setDraftMismatchPending(null);
            }}
          >
            Start Fresh
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!fixed !inset-0 !w-screen !max-w-none !max-h-none !m-0 !p-0 !translate-x-0 !translate-y-0 !left-0 !top-0 relative rounded-none border-0" style={{ height: '100dvh' }} data-testid="create-review-modal" hideCloseButton>
        {/* Scrollable Content Wrapper - iOS Safari compatible */}
        <div className="h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <DialogTitle className="text-lg">
            {step === 'setup' ? (isFromOrder ? 'Review Your Order' : 'Select Restaurant') : 
             step === 'media' ? 'Add Photos or Videos' : 
             (isFromOrder ? 'Review Your Order' : 'Write a Review')}
          </DialogTitle>
          {step === 'review' && !isFromOrder && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setStep('media')}
              data-testid="button-back"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* STEP 1: SETUP - Restaurant Selection & Service Type */}
          {step === 'setup' && (
            <>
              {/* Restaurant Header */}
              <div className="bg-card rounded-lg p-4 border">
                {isFromOrder ? (
                  <div className="space-y-2">
                    <div>
                      <h3 className="font-bold text-lg" data-testid="text-restaurant-name">{restaurant}</h3>
                      <p className="text-sm text-muted-foreground" data-testid="text-restaurant-location">
                        {(() => {
                          const addr = selectedRestaurant?.address;
                          const city = selectedRestaurant?.city;
                          const state = selectedRestaurant?.state;
                          if (addr && city && state) return `${addr}, ${city}, ${state}`;
                          if (addr && city) return `${addr}, ${city}`;
                          if (addr) return addr;
                          if (city && state) return `${city}, ${state}`;
                          return detectedLocation || 'Location';
                        })()}
                      </p>
                    </div>
                    {orderData && (
                      <div className="flex items-center gap-4 pt-1">
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span data-testid="text-order-date">
                            {new Date(orderData.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span data-testid="text-order-time">
                            {new Date(orderData.orderDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                        <Badge variant="secondary" className="text-xs" data-testid="badge-service-type">
                          {serviceType === 'delivery' ? 'Delivery' : 'Takeout'}
                        </Badge>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="restaurant">Restaurant</Label>
                    <RestaurantSearchInput
                      value={restaurant}
                      onChange={setRestaurant}
                      onRestaurantSelect={setSelectedRestaurant}
                      selectedRestaurant={selectedRestaurant}
                      userLatitude={userLatitude}
                      userLongitude={userLongitude}
                      data-testid="input-restaurant"
                    />
                  </div>
                )}
              </div>

              {/* Service Type Selector - Only show when not from an order */}
              {!isFromOrder && (
                <div className="bg-card rounded-lg p-4 border space-y-3">
                  <Label className="text-sm font-medium">Service Type</Label>
                  <RadioGroup
                    value={serviceType}
                    onValueChange={(value) => setServiceType(value as 'dine-in' | 'takeout' | 'delivery')}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="dine-in" id="dine-in" data-testid="select-service-type-dine-in" />
                        <Label htmlFor="dine-in" className="font-normal cursor-pointer">Dine Out</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="takeout" id="takeout" data-testid="select-service-type-takeout" />
                        <Label htmlFor="takeout" className="font-normal cursor-pointer">Takeout</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="delivery" id="delivery" data-testid="select-service-type-delivery" />
                        <Label htmlFor="delivery" className="font-normal cursor-pointer">Delivery</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Continue Button */}
              <div className="pt-4">
                <Button
                  onClick={() => setStep('review')}
                  disabled={!selectedRestaurant?.id}
                  className="w-full"
                  size="lg"
                  data-testid="button-continue-to-review"
                >
                  {!selectedRestaurant?.id ? 'Select a Restaurant to Continue' : 'Continue'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* REVIEW FORM - Single step after restaurant selection */}
          {step === 'review' && (
            <>
          {/* Restaurant Header - Compact summary with cuisine badge */}
          <div className="bg-card rounded-lg p-3 border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base" data-testid="text-restaurant-name-summary">
                  {selectedRestaurant?.name || restaurant}
                </h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span>{serviceType === 'dine-in' ? 'Dine Out' : serviceType === 'takeout' ? 'Takeout' : 'Delivery'}</span>
                  {selectedRestaurant?.city && (
                    <>
                      <span>•</span>
                      <span>{selectedRestaurant.city}</span>
                    </>
                  )}
                </div>
                {hasCuisine ? (
                  <div className="mt-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCuisine("");
                        setTags(prev => prev.filter(t => !cuisineTags.some(ct => ct.label === t)));
                      }}
                      className="inline-flex"
                      data-testid="cuisine-badge"
                    >
                      <Badge variant="secondary" className="text-xs">
                        {selectedCuisine}
                      </Badge>
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 space-y-1">
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Select cuisine type</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="cuisine-picker">
                      {['American', 'Mexican', 'Italian', 'Chinese', 'Japanese', 'Thai', 'Indian', 'Mediterranean'].map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => {
                            setSelectedCuisine(c);
                            setTags(prev => {
                              const withoutCuisine = prev.filter(t => !cuisineTags.some(ct => ct.label === t));
                              return withoutCuisine.includes(c) ? withoutCuisine : [...withoutCuisine, c];
                            });
                          }}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-primary/40 text-muted-foreground hover-elevate"
                          data-testid={`cuisine-pick-${c.toLowerCase()}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('setup')}
                className="text-xs"
                data-testid="button-edit-restaurant"
              >
                Edit
              </Button>
            </div>
          </div>

          {/* ORDER-BASED REVIEW: Per-Item Review Cards */}
          {isFromOrder && dishRatings.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-primary" />
                <Label className="text-sm font-semibold">Rate Your Items</Label>
                <Badge variant="secondary" className="text-xs ml-auto">
                  {dishRatings.filter(d => d.rating !== null).length}/{dishRatings.length} rated
                </Badge>
              </div>
              
              {dishRatings.map((dish, index) => (
                <div key={dish.id} className="bg-card rounded-lg border overflow-visible" data-testid={`dish-card-${index}`}>
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      {/* Dish photo area */}
                      <div className="flex-shrink-0">
                        {dish.mediaUrl ? (
                          <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                            {dish.mediaType === 'video' ? (
                              <video src={dish.mediaUrl} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                            ) : (
                              <img src={dish.mediaUrl} alt={dish.itemName} className="w-full h-full object-cover" />
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                if (dish.mediaUrl) URL.revokeObjectURL(dish.mediaUrl);
                                setDishRatings(prev => prev.map(d => 
                                  d.id === dish.id ? { ...d, mediaUrl: null, mediaType: null, mediaFile: null } : d
                                ));
                              }}
                              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => dishPhotoInputRefs.current[dish.id]?.click()}
                            className="w-20 h-20 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 flex flex-col items-center justify-center gap-1 hover-elevate transition-colors"
                            data-testid={`dish-add-photo-${index}`}
                          >
                            <Camera className="w-5 h-5 text-primary/60" />
                            <span className="text-[10px] text-primary/60 font-medium">+5</span>
                          </button>
                        )}
                        <input
                          ref={(el) => { dishPhotoInputRefs.current[dish.id] = el; }}
                          type="file"
                          accept="image/*,video/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const type = file.type.startsWith('video/') ? 'video' : 'photo';
                            const url = URL.createObjectURL(file);
                            setDishRatings(prev => prev.map(d =>
                              d.id === dish.id ? { ...d, mediaUrl: url, mediaType: type, mediaFile: file } : d
                            ));
                            setMediaDeltaAmount(type === 'video' ? 10 : 5);
                            setShowMediaDelta(true);
                            setTimeout(() => setShowMediaDelta(false), 2200);
                            e.target.value = '';
                          }}
                          data-testid={`dish-photo-input-${index}`}
                        />
                      </div>
                      
                      {/* Item name and rating */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate" data-testid={`dish-name-${index}`}>{dish.itemName}</span>
                            {dish.isTheBest && (
                              <Badge variant="default" className="text-[10px] flex-shrink-0">Best</Badge>
                            )}
                          </div>
                          {(() => {
                            const orderItem = orderData?.items?.find((item: any) => (item.lineItemId || item.itemId) === dish.id);
                            const options = orderItem?.options as string[] | undefined;
                            return options && options.length > 0 ? (
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                {options.slice(0, 3).join(', ')}{options.length > 3 ? ` +${options.length - 3}` : ''}
                              </p>
                            ) : null;
                          })()}
                        </div>
                        
                        {/* Rating buttons */}
                        <div className="flex items-center gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant={dish.rating === 'loved' ? 'default' : 'outline'}
                            onClick={() => {
                              setDishRatings(prev => prev.map(d =>
                                d.id === dish.id ? { ...d, rating: 'loved' } : d
                              ));
                              if (!dish.rating) {
                                setItemRatingDeltaAmount(5);
                                setShowItemRatingDelta(true);
                                setTimeout(() => setShowItemRatingDelta(false), 2200);
                              }
                            }}
                            data-testid={`dish-rating-loved-${index}`}
                          >
                            <Heart className="w-3 h-3 mr-1" />
                            Loved
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={dish.rating === 'fine' ? 'default' : 'outline'}
                            onClick={() => {
                              setDishRatings(prev => prev.map(d =>
                                d.id === dish.id ? { ...d, rating: 'fine' } : d
                              ));
                              if (!dish.rating) {
                                setItemRatingDeltaAmount(5);
                                setShowItemRatingDelta(true);
                                setTimeout(() => setShowItemRatingDelta(false), 2200);
                              }
                            }}
                            data-testid={`dish-rating-fine-${index}`}
                          >
                            <Meh className="w-3 h-3 mr-1" />
                            OK
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={dish.rating === 'disliked' ? 'default' : 'outline'}
                            onClick={() => {
                              setDishRatings(prev => prev.map(d =>
                                d.id === dish.id ? { ...d, rating: 'disliked' } : d
                              ));
                              if (!dish.rating) {
                                setItemRatingDeltaAmount(5);
                                setShowItemRatingDelta(true);
                                setTimeout(() => setShowItemRatingDelta(false), 2200);
                              }
                            }}
                            data-testid={`dish-rating-disliked-${index}`}
                          >
                            <Frown className="w-3 h-3 mr-1" />
                            Nope
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Brief note - show after rating is selected */}
                    {dish.rating && (
                      <div className="pt-1">
                        <Input
                          value={dish.notes}
                          onChange={(e) => {
                            setDishRatings(prev => prev.map(d =>
                              d.id === dish.id ? { ...d, notes: e.target.value } : d
                            ));
                          }}
                          placeholder={dish.rating === 'loved' ? "What made it great?" : dish.rating === 'disliked' ? "What went wrong?" : "Any thoughts?"}
                          className="text-sm"
                          maxLength={150}
                          data-testid={`dish-notes-${index}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ORDER-BASED REVIEW: General Media Section (overall experience) */}
          {isFromOrder && (
            <div className="bg-card rounded-lg p-4 border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Overall Experience Media</Label>
                </div>
                <span className="text-xs text-muted-foreground">Optional</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Add a video or photos of the overall experience, restaurant ambiance, or a quick review summary.
              </p>
              
              {/* General media preview */}
              {mediaUploads.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {mediaUploads.map((media) => (
                    <div key={media.id} className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-muted">
                      {media.type === 'video' ? (
                        <video src={media.url} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                      ) : (
                        <img src={media.url} alt="" className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(media.id)}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isClassifying}
                  className="flex-1"
                  data-testid="button-general-camera"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Camera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isClassifying}
                  className="flex-1"
                  data-testid="button-general-gallery"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Gallery
                </Button>
              </div>
              
              {/* Hidden file inputs for general media */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*,video/*"
                capture="environment"
                className="hidden"
                onChange={handleFileChange}
                data-testid="input-camera"
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                onChange={handleFileChange}
                data-testid="input-gallery"
              />
            </div>
          )}

          {/* NON-ORDER REVIEW: Original camera/gallery capture */}
          {!isFromOrder && (
            <>
              <div className="bg-card rounded-lg p-4 border space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Add Photos or Videos</Label>
                  {mediaUploads.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {mediaUploads.length} added
                    </Badge>
                  )}
                </div>
                
                {mediaUploads.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {mediaUploads.map((media) => (
                      <div key={media.id} className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-muted">
                        {media.type === 'video' ? (
                          <video src={media.url} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                        ) : (
                          <img src={media.url} alt="" className="w-full h-full object-cover" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(media.id)}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {media.classification?.contentType && media.classification.contentType !== 'general' && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 text-center truncate">
                            {media.classification.contentType.replace('-', ' ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {mediaUploads.length === 0 && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={isClassifying}
                      className="flex-1"
                      data-testid="button-camera"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Camera
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isClassifying}
                      className="flex-1"
                      data-testid="button-gallery"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Gallery
                    </Button>
                  </div>
                )}
                
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*,video/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="input-camera"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="input-gallery"
                />
                
                {isClassifying && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing your photo...
                  </div>
                )}
              </div>

              {/* Per-Media Food Item Name & Rating - When AI detects food-item or AI unavailable (non-order reviews only) */}
              {showFoodItemSection && (
                <div className="bg-card rounded-lg p-4 border space-y-3">
                  {(hasFoodItemUploads
                    ? mediaUploads.filter(m => m.classification?.contentType === 'food-item')
                    : mediaUploads
                  ).map((media, index) => {
                      // Per-media search state
                      const searchQuery = menuSearchQuery[media.id] ?? '';
                      const isSearchOpen = menuSearchOpen[media.id] ?? false;

                      // Filter menu items for this media's search query
                      const filteredItems = searchQuery.trim()
                        ? allMenuItems.filter(item =>
                            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.category.toLowerCase().includes(searchQuery.toLowerCase())
                          ).slice(0, 15)
                        : allMenuItems.slice(0, 15);

                      return (
                        <div key={media.id} className="space-y-3">
                          {index > 0 && <Separator />}
                          <div className="flex gap-3">
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                              {media.type === 'video' ? (
                                <video src={media.url} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                              ) : (
                                <img src={media.url} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <Utensils className="w-4 h-4 text-primary flex-shrink-0" />
                                {isTier2Restaurant && !aiClassificationUnavailable ? (
                                  /* Tier 2 with AI available: menu selector — search from the restaurant's actual menu */
                                  <div className="relative flex-1">
                                    {media.menuItemId && media.itemName ? (
                                      /* Selected state: show item name with a change link */
                                      <div className="flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-muted/50">
                                        <span className="text-sm font-medium truncate">{media.itemName}</span>
                                        <button
                                          type="button"
                                          className="text-xs text-primary flex-shrink-0"
                                          onClick={() => {
                                            setMediaUploads(prev => prev.map(m =>
                                              m.id === media.id ? { ...m, menuItemId: undefined, itemName: '' } : m
                                            ));
                                            setMenuSearchQuery(prev => ({ ...prev, [media.id]: '' }));
                                            setMenuSearchOpen(prev => ({ ...prev, [media.id]: true }));
                                          }}
                                        >
                                          Change
                                        </button>
                                      </div>
                                    ) : isLoadingMenu ? (
                                      /* Loading: skeleton pills */
                                      <div className="flex gap-1">
                                        {[1, 2, 3].map(i => (
                                          <div key={i} className="h-7 w-20 bg-muted animate-pulse rounded-full" />
                                        ))}
                                      </div>
                                    ) : (
                                      /* Search input */
                                      <>
                                        <div className="relative">
                                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                          <input
                                            type="text"
                                            placeholder="Search menu items..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                              setMenuSearchQuery(prev => ({ ...prev, [media.id]: e.target.value }));
                                              setMenuSearchOpen(prev => ({ ...prev, [media.id]: true }));
                                            }}
                                            onFocus={() => setMenuSearchOpen(prev => ({ ...prev, [media.id]: true }))}
                                            className="w-full pl-8 pr-7 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            data-testid={`input-item-name-${index}`}
                                          />
                                          {searchQuery && (
                                            <button
                                              type="button"
                                              onClick={() => setMenuSearchQuery(prev => ({ ...prev, [media.id]: '' }))}
                                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                        {isSearchOpen && filteredItems.length > 0 && (
                                          <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-md bg-background shadow-md max-h-44 overflow-y-auto">
                                            {filteredItems.map((item) => (
                                              <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => {
                                                  setMediaUploads(prev => prev.map(m =>
                                                    m.id === media.id ? { ...m, menuItemId: item.id, itemName: item.name } : m
                                                  ));
                                                  setMenuSearchQuery(prev => ({ ...prev, [media.id]: '' }));
                                                  setMenuSearchOpen(prev => ({ ...prev, [media.id]: false }));
                                                }}
                                                className={cn(
                                                  "w-full px-3 py-2 text-left text-sm flex items-center justify-between hover-elevate",
                                                  media.menuItemId === item.id && "bg-primary/10"
                                                )}
                                              >
                                                <span className="truncate">{item.name}</span>
                                                <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">{item.category}</span>
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  /* Non-Tier 2: plain free-text input */
                                  <Input
                                    value={media.itemName || media.classification?.detectedItems?.[0] || ''}
                                    onChange={(e) => {
                                      const newUploads = mediaUploads.map(m =>
                                        m.id === media.id ? { ...m, itemName: e.target.value } : m
                                      );
                                      setMediaUploads(newUploads);
                                    }}
                                    placeholder="Name this dish..."
                                    className="h-8 text-sm font-medium"
                                    data-testid={`input-item-name-${index}`}
                                  />
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground mr-1">How was it?</span>
                                <Button type="button" size="sm" variant={media.itemRating === 'loved' ? 'default' : 'outline'} className="h-7 text-xs px-2" onClick={() => { setMediaUploads(prev => prev.map(m => m.id === media.id ? { ...m, itemRating: 'loved' as const } : m)); }} data-testid={`rating-loved-${index}`}><Heart className="w-3 h-3 mr-1" />Loved</Button>
                                <Button type="button" size="sm" variant={media.itemRating === 'fine' ? 'default' : 'outline'} className="h-7 text-xs px-2" onClick={() => { setMediaUploads(prev => prev.map(m => m.id === media.id ? { ...m, itemRating: 'fine' as const } : m)); }} data-testid={`rating-fine-${index}`}><Meh className="w-3 h-3 mr-1" />OK</Button>
                                <Button type="button" size="sm" variant={media.itemRating === 'disliked' ? 'default' : 'outline'} className="h-7 text-xs px-2" onClick={() => { setMediaUploads(prev => prev.map(m => m.id === media.id ? { ...m, itemRating: 'disliked' as const } : m)); }} data-testid={`rating-disliked-${index}`}><Frown className="w-3 h-3 mr-1" />Nope</Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Add More Button - Shows when media already exists (non-order reviews) */}
              {mediaUploads.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 py-2 text-primary"
                      data-testid="button-add-more-media"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center relative">
                        <Camera className="w-5 h-5 text-primary-foreground" />
                        <Plus className="w-3 h-3 text-primary-foreground absolute bottom-1.5 right-1.5" />
                      </div>
                      <span className="text-sm font-medium">Add more</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 p-2" align="start">
                    <div className="flex flex-col gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => cameraInputRef.current?.click()} className="justify-start" data-testid="add-more-camera"><Camera className="w-4 h-4 mr-2" />Camera</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} className="justify-start" data-testid="add-more-gallery"><ImageIcon className="w-4 h-4 mr-2" />Gallery</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </>
          )}

          {/* Tag Selection - Using QuickTagSelector with collapsible categories */}
          <QuickTagSelector
            selectedTags={tags}
            onTagsChange={setTags}
            contentType={dominantContentType}
            aiSuggestedTags={classificationSuggestedTags}
            serviceType={serviceType}
          />

          {/* Your Recommendations - Highlighted Section */}
          <div className="rounded-lg p-4 border-2 border-primary/20 space-y-2" style={{ background: 'hsl(var(--primary) / 0.04)' }}>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Your Recommendations
              </Label>
              {reviewText.length === 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  Helps others decide
                </Badge>
              )}
            </div>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="What should others order here? Any tips for first-timers?"
              className="min-h-24 resize-none border-primary/10 bg-background"
              maxLength={500}
              data-testid="input-review-text"
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              <span>{reviewText.length}/500</span>
            </div>
          </div>

          {/* Overall Experience Rating - After text entry */}
          <div className="bg-card rounded-lg p-4 border space-y-4 relative">
            <InlineKudozDelta show={showVibeDelta} amount={vibeDeltaAmount} position="top-right" />
            
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Overall Experience
              </Label>
              <span className="font-bold text-lg text-primary">
                {orderRating[0].toFixed(1)}
              </span>
            </div>
            
            <div className="relative pt-2 pb-4">
              <Slider
                value={orderRating}
                onValueChange={(newValue) => {
                  setOrderRating(newValue);
                  if (!hasRatedVibe && newValue[0] !== 5.0) {
                    setHasRatedVibe(true);
                    setVibeDeltaAmount(5);
                    setShowVibeDelta(true);
                    setTimeout(() => setShowVibeDelta(false), 2200);
                  }
                }}
                min={1}
                max={10}
                step={0.1}
                className="w-full"
                data-testid="slider-order-rating"
              />
              
              {/* Prior review notches */}
              <div className="absolute top-0 left-0 right-0 h-2 pointer-events-none">
                {priorReviews.map((review, idx) => (
                  <div
                    key={review.reviewId || idx}
                    className="absolute top-0 w-0.5 h-2 bg-muted-foreground/40"
                    style={{ left: `${((review.score - 1) / 9) * 100}%` }}
                    title={`${review.restaurant}: ${review.score}`}
                  />
                ))}
              </div>
            </div>
            
            {priorReviews.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {priorReviews.map((review) => {
                  const diff = orderRating[0] - review.score;
                  if (Math.abs(diff) < 0.5) {
                    return (
                      <span key={review.reviewId}>
                        Similar to {review.restaurant} ({review.score.toFixed(1)})
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>

          {/* Tag Friends */}
          <div className="bg-card rounded-lg border p-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Tag Friends</Label>
              <TagFriendsPopover
                following={followingData?.users ?? []}
                selected={companionNames}
                onAdd={(id) => setCompanionNames(prev => [...prev, id])}
                onRemove={(id) => setCompanionNames(prev => prev.filter(x => x !== id))}
              />
            </div>
            <SelectedFriendChips
              following={followingData?.users ?? []}
              selected={companionNames}
              onRemove={(id) => setCompanionNames(prev => prev.filter(x => x !== id))}
            />
          </div>

          {/* Bottom Action Bar: KudozModule | Requirements Checklist | Post Button */}
          <div className="space-y-2 pt-2" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}>
            {/* Save Draft - compact secondary action */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveDraft}
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                data-testid="button-save-draft"
              >
                <Save className="w-3 h-3 mr-1" />
                Save Draft
              </Button>
            </div>

            {/* Main bar */}
            <div className="flex items-center gap-3 bg-card rounded-lg border p-3">
              {/* Left: Kudoz disc with progress ring */}
              <SectionErrorBoundary section="KudozModule" fallback={<div className="w-10 h-10" />}>
                <KudozModule
                  earned={kudozBreakdown.earned}
                  total={kudozBreakdown.total}
                  showProgress={true}
                  animating={kudozAnimating}
                  size="sm"
                />
              </SectionErrorBoundary>

              {/* Centre: 5-item requirement checklist */}
              <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1 min-w-0">
                {([
                  { met: hasRestaurant, label: 'Restaurant' },
                  { met: hasCuisine,    label: 'Cuisine' },
                  { met: hasText,       label: 'Review (10+)' },
                  { met: hasTag,        label: 'Tag' },
                  { met: hasSlider,     label: 'Rating' },
                ] as { met: boolean; label: string }[]).map(({ met, label }) => (
                  <div key={label} className="flex items-center gap-1 min-w-0">
                    <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center ${met ? 'bg-green-500' : 'bg-muted'}`}>
                      {met && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className={`text-[10px] leading-tight truncate ${met ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                  </div>
                ))}
              </div>

              {/* Right: Post button */}
              <Button
                onClick={handleSubmit}
                disabled={!canPost || isSubmitting || isResolvingRestaurant}
                size="default"
                className="flex-shrink-0"
                data-testid="button-post-review"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isResolvingRestaurant ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Post'
                )}
              </Button>
            </div>
          </div>
            </>
          )}
        </div>
        </div>
      </DialogContent>
      
      {/* Kudoz Celebration Modal - Effort-weighted */}
      <KudozCelebration
        show={showCelebration}
        amount={kudozBreakdown.earned}
        level={celebrationLevel}
        onComplete={() => setShowCelebration(false)}
      />
    </Dialog>
    </>
  );
}
